import { Link, NavLink, Outlet } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useWaves } from "../context/WaveContext";
import { LayoutDashboard, Store, CalendarClock, FileText, Users, ClipboardList, LogOut, Layers, UserCircle2, Receipt, History, Wallet, Download } from "lucide-react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import WaveSelector from "./WaveSelector";

export default function Layout() {
  const { user, logout } = useAuth();
  const { currentWave } = useWaves();
  const isAdmin = user?.role === "admin";
  const [exporting, setExporting] = useState(false);

  const handleBackup = async () => {
    setExporting(true);
    try {
      const res = await api.get("/export/all", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.setAttribute("download", m?.[1] || "export_dinning.zip");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Copia de seguridad descargada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar la copia");
    } finally {
      setExporting(false);
    }
  };

  // Page-key based filtering. If user has allowed_pages non-empty, restrict to those.
  // Admin always sees everything; viewer/editor restricted unless list is empty (legacy).
  const allowedPages = (user?.allowed_pages || []);
  const hasPage = (key) => isAdmin || allowedPages.length === 0 || allowedPages.includes(key);

  const allNav = [
    { key: "dashboard",   to: "/dashboard",   label: "Resumen",      icon: LayoutDashboard, testId: "nav-dashboard" },
    { key: "restaurants", to: "/restaurants", label: "Restaurantes", icon: Store,           testId: "nav-restaurants" },
    { key: "events",      to: "/events",      label: "Eventos",      icon: CalendarClock,   testId: "nav-events" },
    { key: "attendance",  to: "/attendance",  label: "Asistencia",   icon: ClipboardList,   testId: "nav-attendance" },
    { key: "invoices",    to: "/invoices",    label: "Facturas",     icon: FileText,        testId: "nav-invoices" },
    { key: "cashout",     to: "/cashout",     label: "Cashout",      icon: Wallet,          testId: "nav-cashout" },
    { key: "budget",      to: "/presupuesto", label: "Presupuesto",  icon: Receipt,         testId: "nav-presupuesto" },
    { key: "employees",   to: "/employees",   label: "Empleados",    icon: UserCircle2,     testId: "nav-employees" },
  ];
  const navItems = allNav.filter((i) => hasPage(i.key));
  const adminItems = isAdmin
    ? [
        { to: "/waves", label: "Waves", icon: Layers, testId: "nav-waves" },
        { to: "/users", label: "Usuarios", icon: Users, testId: "nav-users" },
        { to: "/audit", label: "Auditoría", icon: History, testId: "nav-audit" },
      ]
    : [];

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]" data-testid="app-layout">
      <aside className="w-64 border-r border-zinc-200 bg-white sticky top-0 h-screen flex flex-col">
        <div className="px-6 py-6 border-b border-zinc-200">
          <Link to="/dashboard" className="block">
            <p className="label-eyebrow">Restaurant Billing</p>
            <h1 className="font-heading text-2xl tracking-tight mt-1">Convivium<span className="text-zinc-400">/</span>fact</h1>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {[...navItems, ...adminItems].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <item.icon className="h-4 w-4" strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-zinc-200">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-zinc-200" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-zinc-200" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" data-testid="user-name">{user?.name || user?.email}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider" data-testid="user-role">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-wider py-2 border border-zinc-200 hover:bg-zinc-100 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="border-b border-zinc-200 bg-white px-6 md:px-10 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-4" data-testid="header-wave">
            <Layers className="h-6 w-6 text-zinc-400" strokeWidth={1.5} />
            <div>
              <p className="label-eyebrow">Wave activa</p>
              <p className="font-heading text-2xl tracking-tight leading-none mt-1">{currentWave?.name || "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <button
                onClick={handleBackup}
                disabled={exporting}
                data-testid="header-backup-button"
                title="Descargar copia de seguridad completa (ZIP)"
                className="flex items-center gap-2 text-xs uppercase tracking-wider px-3 py-2 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" strokeWidth={1.5} />
                {exporting ? "Generando…" : "Backup"}
              </button>
            )}
            <WaveSelector />
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
